import { Location } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { HeaderService } from 'core';
import { DateTime } from 'luxon';
import { CustomerConfigurationService, CustomerConfigurationState } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-configure-customer-order-notes',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatDatepickerModule, MatFormFieldModule, MatInputModule, MatSlideToggleModule, RouterLink, TranslocoModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './configure-customer-order-notes.html',
  styleUrl: './configure-customer-order-notes.scss',
})
export class ConfigureCustomerOrderNotes implements OnInit, OnDestroy {
  readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly today = DateTime.now().setZone('Europe/Rome').startOf('day');

  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  readonly customer = computed(() => this.configuration.proposalId() === this.proposalId ? this.configuration.customer() : null);
  readonly form = inject(FormBuilder).nonNullable.group({
    title: [''],
    text: [''],
    date: [{ value: this.today, disabled: true }],
    addToAuthorization: [true],
  });

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));
    this.configuration.restoreFlowState(this.location.getState() as Partial<CustomerConfigurationState> | null, this.proposalId);
    const draft = this.configuration.orderNote();
    if (this.customer() && draft) {
      const date = DateTime.fromISO(draft.date, { zone: 'Europe/Rome' });
      // Ripristina solo la nota già inserita dall'utente nella bozza, senza prefill da Cribis.
      this.form.setValue({ ...draft, date: date.isValid ? date : this.today });
    }
    if (this.customer()) {
      this.saveDraft();
    }
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.customer()) {
        this.saveDraft();
      }
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  continue(): void {
    if (!this.customer() || !this.configuration.destinationForConfiguration()) {
      return;
    }
    this.saveDraft();
    // TODO: Inviare la nota alla futura API.
    void this.router.navigate(['/new-order-proposal/configure-customer/gdpr'], {
      queryParams: { id: this.proposalId },
      state: this.configuration.getFlowState(),
    });
  }

  private saveDraft(): void {
    const value = this.form.getRawValue();
    this.configuration.orderNote.set({
      title: value.title,
      text: value.text,
      date: value.date.toISODate() ?? this.today.toISODate()!,
      addToAuthorization: value.addToAuthorization,
    });
  }
}
